(() => {
  if (customElements.get('vellure-measurements')) return;
  class VellureMeasurements extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.config = JSON.parse(this.querySelector('[data-config]').textContent);
      this.unit = 'cm';
      this.values = {};
      this.fields = [...this.querySelectorAll('[data-measure-input]')];
      this.isSuit = this.dataset.mode === 'suit';
      this.services = [...this.querySelectorAll('[data-service]')];
      this.notes = this.querySelector('[data-alteration-notes]');
      this.pricedServices = [...this.querySelectorAll('[data-priced-service]')];
      this.quantities = [...this.querySelectorAll('[data-service-quantity]')];
      this.panel = this.querySelector('[data-panel]');
      this.error = this.querySelector('[data-error]');
      this.form = this.closest('form');
      this.querySelectorAll('[data-unit]').forEach(button => button.addEventListener('click', () => this.setUnit(button.dataset.unit)));
      this.fields.forEach(input => {
        input.addEventListener('focus', () => this.highlight(input));
        input.addEventListener('input', () => {
          const number = input.valueAsNumber;
          if (input.value !== '' && Number.isFinite(number)) this.values[input.dataset.key] = this.unit === 'cm' ? number : number * 2.54;
          else delete this.values[input.dataset.key];
          this.error.hidden = true;
          this.highlight(input);
          this.updateProgress();
        });
      });
      this.services.forEach(input => input.addEventListener('change', () => {
        this.toggle();
        if (input.dataset.service === 'additional' && input.checked) {
          const heading = this.querySelector('[data-additional-heading]');
          heading.focus({ preventScroll: true });
          heading.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
        }
      }));
      this.pricedServices.forEach(input => input.addEventListener('change', () => {
        if (input.checked && input.dataset.exclusive) {
          this.pricedServices.forEach(other => {
            if (other !== input && other.dataset.exclusive === input.dataset.exclusive) other.checked = false;
          });
        }
        this.updateEstimate();
      }));
      this.quantities.forEach(input => input.addEventListener('input', () => this.updateEstimate()));
      this.notes?.addEventListener('input', () => {
        this.notes.setCustomValidity('');
        this.error.hidden = true;
      });
      this.form?.addEventListener('submit', event => {
        if (!this.validateMeasurements()) { event.preventDefault(); event.stopImmediatePropagation(); }
      }, true);
      this.toggle();
      this.highlight(this.fields.find(input => !input.disabled));
    }
    hasService(service) { return this.services.some(input => input.dataset.service === service && input.checked); }
    get enabled() { return !this.isSuit || this.services.some(input => input.checked); }
    toggle() {
      this.fields.forEach(input => {
        const active = !this.isSuit || this.hasService('additional') || (input.dataset.key === 'inside' && this.hasService('trousers')) || (input.dataset.key === 'sleeve' && this.hasService('sleeves'));
        input.disabled = !active;
        input.required = active && input.dataset.optional !== 'true';
        input.closest('[data-field-wrap]').hidden = !active;
      });
      const hasMeasurements = this.fields.some(input => !input.disabled);
      this.panel.hidden = !hasMeasurements;
      this.querySelector('[data-unit-property]').disabled = !hasMeasurements;
      this.querySelectorAll('[data-measure-property]:not([data-unit-property])').forEach(input => { input.disabled = !this.enabled; });
      this.querySelector('[data-progress]').hidden = !hasMeasurements;
      if (this.notes) {
        const additional = this.hasService('additional');
        this.querySelector('[data-additional-panel]').hidden = !additional;
        this.querySelector('[data-quote-status]').disabled = !additional;
        this.notes.disabled = !additional;
        this.notes.setCustomValidity('');
        this.pricedServices.forEach(input => { input.disabled = !additional; });
        this.updateEstimate();
      }
      this.error.hidden = true;
      this.form?.querySelectorAll('shopify-accelerated-checkout').forEach(element => { element.hidden = this.enabled; });
      this.updateProgress();
      this.highlight(this.fields.find(input => !input.disabled));
    }
    updateEstimate() {
      if (!this.notes) return;
      const active = this.hasService('additional');
      const selected = this.pricedServices.filter(input => input.checked && !input.disabled);
      const other = selected.some(input => input.dataset.price === undefined);
      this.quantities.forEach(input => {
        const checked = selected.some(service => service.dataset.pricedService === input.dataset.serviceQuantity);
        input.disabled = !active || !checked;
        input.required = active && checked;
        input.closest('[data-quantity-wrap]').hidden = !active || !checked;
      });
      const invalidQuantity = this.quantities.some(input => !input.disabled && !input.validity.valid);
      const total = selected.reduce((sum, input) => {
        const quantity = this.quantities.find(field => field.dataset.serviceQuantity === input.dataset.pricedService);
        return sum + Number(input.dataset.price || 0) * (quantity ? Number(quantity.value) || 0 : 1);
      }, 0);
      const amount = new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(total / 100);
      const hasPricedWork = selected.some(input => input.dataset.price !== undefined);
      const summary = invalidQuantity ? this.config.quantityError : !selected.length ? this.config.chooseEstimate : (hasPricedWork ? this.config.from + ' ' + amount + (other ? ' + ' + this.config.quoteExtra : '') : this.config.quoteExtra);
      this.querySelector('[data-estimate]').textContent = summary;
      const property = this.querySelector('[data-estimate-property]');
      property.disabled = !active || !selected.length || invalidQuantity;
      property.value = summary;
      this.pricedServices[0].required = false;
      this.notes.required = active && other;
      this.notes.setCustomValidity('');
      this.error.hidden = true;
    }
    setUnit(unit) {
      if (unit === this.unit) return;
      this.unit = unit;
      this.querySelectorAll('[data-unit]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.unit === unit)));
      this.querySelector('[data-unit-property]').value = unit;
      this.querySelectorAll('[data-unit-label]').forEach(label => { label.textContent = unit; });
      this.fields.forEach(input => {
        const canonical = this.values[input.dataset.key];
        input.value = canonical === undefined ? '' : Number((unit === 'cm' ? canonical : canonical / 2.54).toFixed(2));
        input.name = `properties[${input.dataset.property} (${unit})]`;
        input.min = input.dataset.key === 'heel' ? '0' : unit === 'cm' ? '0.1' : '0.04';
        input.max = unit === 'cm' ? '400' : '157.48';
      });
      this.highlight(this.fields.find(input => !input.disabled && input.dataset.key === this.selected) || this.fields.find(input => !input.disabled));
      this.updateProgress();
    }
    highlight(input) {
      if (!input) return;
      this.selected = input.dataset.key;
      const definition = this.config.fields[input.dataset.key];
      this.querySelector('[data-selected-label]').textContent = definition.label;
      this.querySelector('[data-selected-help]').textContent = definition.help;
      this.querySelector('[data-selected-value]').textContent = `${input.value || '—'} ${this.unit}`;
      this.querySelectorAll('[data-measure-line]').forEach(line => { line.classList.toggle('is-active', line.dataset.measureLine === input.dataset.line); });
    }
    updateProgress() {
      const required = this.fields.filter(input => input.required && !input.disabled);
      const count = required.filter(input => input.value !== '' && input.validity.valid).length;
      this.querySelector('[data-progress]').textContent = this.config.progress.replace('[count]', count).replace('[total]', required.length);
    }
    validateMeasurements() {
      if (!this.enabled) return true;
      if (this.hasService('additional') && !this.pricedServices.some(input => input.checked && !input.disabled)) {
        this.error.textContent = this.config.selectWork;
        this.error.hidden = false;
        this.pricedServices[0].closest('details').open = true;
        this.pricedServices[0].focus();
        return false;
      }
      if (this.notes && this.notes.required) this.notes.setCustomValidity(this.notes.value.trim() ? '' : this.config.detailsError);
      const invalid = [...this.quantities, ...this.pricedServices, ...this.fields, this.notes].find(input => input && !input.disabled && !input.validity.valid);
      if (!invalid) return true;
      this.error.textContent = this.config.error;
      this.error.hidden = false;
      const group = invalid.closest('details');
      if (group) group.open = true;
      invalid.focus();
      invalid.reportValidity();
      return false;
    }
  }
  customElements.define('vellure-measurements', VellureMeasurements);
})();
